<template>
  <div>
    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <b-card class="card_body_bottom">
        <template #header>
          <b-row class="flex-row justify-content-start align-items-center" style="width: 100%">
            <b-col cols="12" xl="6" lg="6" md="6" sm="12" class="d-flex justify-content-start align-items-center" style="gap:5px">
              <h4 class="mb-0">Kare Infina Report</h4>
              <b-button
                variant="flat-secondary"
                class="btn-icon rounded-circle"
                :disabled="selectedAccounts.length === 0"
                @click="fetchData"
                v-b-tooltip.hover="'Refresh Report'"
              >
                <feather-icon icon="RefreshCcwIcon" />
              </b-button>
            </b-col>
          </b-row>
        </template>

        <b-card-body>
          <b-row class="align-items-end">
            <!-- Accounts -->
            <b-col cols="12" md="6" xl="4" lg="4" sm="12">
              <label class="compact_label">Accounts</label>
              <v-select
                class="compact_form_vue-multi-select"
                v-model="selectedAccounts"
                label="AccountName"
                :options="optAccounts"
                multiple
                :close-on-select="false"
                placeholder="Select account(s)"
              />
            </b-col>

            <!-- Select all accounts -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12" style="padding-bottom: 0.4rem;">
              <b-form-checkbox v-model="allAccountsSelected" @change="toggleAllAccounts">
                All Accounts
              </b-form-checkbox>
            </b-col>

            <!-- Start Date -->
            <b-col cols="12" md="6" xl="3" lg="3" sm="12">
              <label class="compact_label">Start Date</label>
              <b-form-datepicker
                v-model="startDate"
                placeholder="Start date"
                :start-weekday="1"
                :hide-header="true"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>

            <!-- End Date -->
            <b-col cols="12" md="6" xl="3" lg="3" sm="12">
              <label class="compact_label">End Date</label>
              <b-form-datepicker
                v-model="endDate"
                placeholder="End date"
                :start-weekday="1"
                :hide-header="true"
                :min="startDate"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col cols="12" class="d-flex align-items-end flex-wrap" style="gap: 8px;">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="primary"
                size="sm"
                :disabled="selectedAccounts.length === 0"
                @click="fetchData"
              >
                <feather-icon icon="SearchIcon" class="mr-50" />
                <span class="align-middle">Generate Report</span>
              </b-button>

              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-primary"
                size="sm"
                @click="openMappings"
              >
                <feather-icon icon="ListIcon" class="mr-50" />
                <span class="align-middle">Ticker Mappings</span>
              </b-button>

              <b-button
                v-if="rows.length > 0"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-success"
                size="sm"
                :disabled="downloading"
                @click="downloadXlsx"
              >
                <b-spinner v-if="downloading" small class="mr-50" />
                <feather-icon v-else icon="DownloadIcon" class="mr-50" />
                <span class="align-middle">Download XLSX</span>
              </b-button>

              <b-button
                v-if="editedCount > 0"
                variant="flat-secondary"
                size="sm"
                @click="resetEdits"
              >
                <feather-icon icon="RotateCcwIcon" class="mr-50" />
                <span class="align-middle">Reset {{ editedCount }} edit(s)</span>
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <template #overlay v-if="loading">
        <div class="text-center d-flex justify-content-center align-items-start mt-3">
          <b-icon icon="stopwatch" font-scale="3" animation="cylon" />
          <p id="cancel-label">
            <feather-icon icon="TargetIcon" />
            Preparing report...
          </p>
        </div>
      </template>
    </b-overlay>

    <!-- Results -->
    <b-card v-if="rows.length > 0" class="mt-1">
      <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap" style="gap: 6px;">
        <span class="text-muted" style="font-size: 0.8rem;">
          {{ rows.length }} trade(s)
          <span v-if="editedCount > 0" class="text-warning">
            &middot; {{ editedCount }} KIYMET edit(s) apply to this export only
          </span>
        </span>
        <span class="text-muted" style="font-size: 0.75rem;">KIYMET is editable &middot; scroll horizontally to view all columns</span>
      </div>

      <b-alert v-if="unmappedCount > 0" variant="warning" show class="mb-1">
        <div class="alert-body" style="font-size: 0.78rem;">
          {{ unmappedCount }} row(s) have an ISIN that is not in the Kare Ticker Mappings and fall back to
          the security description (highlighted below). Upload a mapping that covers them, or edit KIYMET here.
        </div>
      </b-alert>

      <div class="table-responsive">
        <b-table
          class="compact_table kare-report-table"
          :items="rows"
          :fields="tableColumns"
          :small="true"
          :hover="true"
          :striped="true"
          :bordered="true"
          :fixed="false"
          headVariant="dark"
          table-variant="none"
          :tbody-tr-class="rowClass"
          responsive
          empty-text="No trades found for the selected criteria."
          show-empty
        >
          <template #cell(islemTipi)="data">
            <b-badge :variant="data.value === 'A' ? 'light-success' : 'light-danger'">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(KIYMET)="data">
            <b-form-input
              v-model="rows[data.index].KIYMET"
              size="sm"
              class="kiymet-input"
              :class="{ 'kiymet-edited': isEdited(data.index) }"
            />
          </template>

          <template #cell(islemMiktari)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value, 0) }}</div>
          </template>
          <template #cell(islemTutari)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value, 2) }}</div>
          </template>
          <template #cell(islemKomOrani)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value, 2) }}</div>
          </template>
          <template #cell(islemKomTutari)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value, 2) }}</div>
          </template>
          <template #cell(bsmvTutari)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value, 2) }}</div>
          </template>
          <template #cell(islemBirimFiyati)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value, 4) }}</div>
          </template>
          <template #cell(islemTarihi)="data">
            <div class="text-nowrap">{{ formatDate(data.value) }}</div>
          </template>
          <template #cell(kiymetTakas)="data">
            <div class="text-nowrap">{{ formatDate(data.value) }}</div>
          </template>
        </b-table>
      </div>
    </b-card>

    <b-alert v-else-if="searched && rows.length === 0" variant="primary" show class="mt-1">
      <div class="alert-body">
        <span>No trades found for the selected criteria.</span>
      </div>
    </b-alert>

    <!-- Ticker Mappings modal -->
    <b-modal
      v-model="mappingModal"
      title="Kare Ticker Mappings"
      size="lg"
      scrollable
      ok-only
      ok-title="Close"
      ok-variant="secondary"
    >
      <b-overlay :show="mappingLoading || uploading" rounded="sm" opacity="0.6">
        <!-- current mapping summary -->
        <div v-if="mapping.upload" class="mb-1">
          <b-alert variant="primary" show class="mb-1">
            <div class="alert-body" style="font-size: 0.78rem;">
              <strong>{{ mapping.upload.FileName || 'Uploaded file' }}</strong><br />
              {{ mapping.upload.RowCount }} ISIN(s)
              &middot; uploaded by {{ mapping.upload.UploadedByName || 'unknown' }}
              on {{ mapping.upload.UploadedAt }}
            </div>
          </b-alert>

          <b-alert v-if="mapping.conflicts && mapping.conflicts.length > 0" variant="warning" show class="mb-1">
            <div class="alert-body" style="font-size: 0.75rem;">
              <strong>{{ mapping.conflicts.length }} ISIN(s) appeared more than once with different names.</strong>
              The last row in the file was used:
              <ul class="mb-0 mt-50 pl-2">
                <li v-for="c in mapping.conflicts" :key="c.isin">
                  {{ c.isin }}: {{ c.names.join(' / ') }} &rarr; <strong>{{ c.used }}</strong>
                </li>
              </ul>
            </div>
          </b-alert>
        </div>

        <b-alert v-else variant="warning" show class="mb-1">
          <div class="alert-body" style="font-size: 0.78rem;">
            No ticker mapping has been uploaded yet. Until one is, KIYMET falls back to the security description.
          </div>
        </b-alert>

        <!-- upload -->
        <b-form-group label="Upload a new mapping (replaces the current one)" class="mb-1">
          <b-form-file
            ref="mappingFile"
            accept=".xls, .xlsx"
            placeholder="Choose an Excel file or drop it here..."
            drop-placeholder="Drop file here..."
            @change="onMappingFile"
          />
          <small class="text-muted">
            Column A = ISIN, column B = name. A header row named "ISIN" is skipped automatically.
          </small>
        </b-form-group>

        <b-form-group v-if="pendingSheetNames.length > 1" label="Sheet" class="mb-1">
          <v-select
            v-model="pendingSheet"
            :options="pendingSheetNames"
            :clearable="false"
          />
        </b-form-group>

        <!-- staged upload preview -->
        <div v-if="pendingRows.length > 0" class="mb-1">
          <b-alert variant="info" show class="mb-1">
            <div class="alert-body" style="font-size: 0.78rem;">
              <strong>{{ pendingRows.length }} ISIN(s)</strong> ready from
              "{{ pendingFileName }}"<span v-if="pendingSheetNames.length > 1"> / {{ pendingSheet }}</span>.
              <span v-if="pendingSkipped > 0"> {{ pendingSkipped }} row(s) without an ISIN or name were skipped.</span>
            </div>
          </b-alert>

          <b-alert v-if="pendingConflicts.length > 0" variant="warning" show class="mb-1">
            <div class="alert-body" style="font-size: 0.75rem;">
              <strong>{{ pendingConflicts.length }} duplicate ISIN(s) with different names.</strong>
              The last row wins:
              <ul class="mb-0 mt-50 pl-2">
                <li v-for="c in pendingConflicts" :key="c.isin">
                  {{ c.isin }}: {{ c.names.join(' / ') }} &rarr; <strong>{{ c.used }}</strong>
                </li>
              </ul>
            </div>
          </b-alert>

          <div class="d-flex" style="gap: 8px;">
            <b-button variant="primary" size="sm" :disabled="uploading" @click="confirmUpload">
              <b-spinner v-if="uploading" small class="mr-50" />
              <span class="align-middle">Replace Mapping</span>
            </b-button>
            <b-button variant="flat-secondary" size="sm" @click="clearPending">Cancel</b-button>
          </div>
        </div>

        <!-- current mapping table -->
        <div v-if="mapping.mappings.length > 0">
          <b-form-input
            v-model="mappingFilter"
            size="sm"
            placeholder="Filter by ISIN or name..."
            class="mb-1"
          />
          <div style="max-height: 320px; overflow-y: auto;">
            <b-table
              class="compact_table kare-mapping-table"
              :items="filteredMappings"
              :fields="mappingColumns"
              :small="true"
              :striped="true"
              :bordered="true"
              headVariant="dark"
              empty-text="No mappings match the filter."
              show-empty
            />
          </div>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormCheckbox,
  BTable, BOverlay, BIcon, BAlert, BSpinner, BBadge, BModal, BFormFile,
  BFormGroup, BFormInput, VBTooltip,
} from 'bootstrap-vue';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import vSelect from 'vue-select';
import moment from 'moment';
import kareInfinaReportStoreModule from './kareInfinaReportStoreModule';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Ripple from 'vue-ripple-directive';

const STORE_MODULE = 'kareInfinaReport';

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormCheckbox,
    BTable, BOverlay, BIcon, BAlert, BSpinner, BBadge, BModal, BFormFile,
    BFormGroup, BFormInput, vSelect, ToastificationContent,
  },

  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  setup() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, kareInfinaReportStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE)) store.unregisterModule(STORE_MODULE);
    });
  },

  data() {
    return {
      selectedAccounts: [],
      optAccounts: [],
      allAccountsSelected: false,
      startDate: null,
      endDate: null,

      rows: [],
      // KIYMET exactly as the server built it, so edits can be detected and reset
      originalKiymet: [],
      loading: false,
      downloading: false,
      searched: false,

      mappingModal: false,
      mappingLoading: false,
      mapping: { upload: null, mappings: [], conflicts: [] },
      mappingFilter: '',

      uploading: false,
      pendingWorkbook: null,
      pendingSheetNames: [],
      pendingSheet: null,
      pendingRows: [],
      pendingConflicts: [],
      pendingSkipped: 0,
      pendingFileName: '',
    };
  },

  computed: {
    tableColumns() {
      const w = (px) => ({ minWidth: px + 'px' });
      return [
        { key: 'islemTipi',        label: 'islemTipi',        thStyle: w(90) },
        { key: 'portfoyKodu',      label: 'portfoyKodu',      thStyle: w(140) },
        { key: 'dovizCinsi',       label: 'dovizCinsi',       thStyle: w(100) },
        { key: 'KIYMET',           label: 'KIYMET',           thStyle: w(170) },
        { key: 'ISIN',             label: 'ISIN',             thStyle: w(140) },
        { key: 'islemMiktari',     label: 'islemMiktari',     thStyle: w(120) },
        { key: 'islemTutari',      label: 'islemTutari',      thStyle: w(130) },
        { key: 'islemKomOrani',    label: 'islemKomOrani',    thStyle: w(120) },
        { key: 'islemKomTutari',   label: 'islemKomTutari',   thStyle: w(130) },
        { key: 'bsmvTutari',       label: 'bsmvTutari',       thStyle: w(110) },
        { key: 'islemBirimFiyati', label: 'islemBirimFiyati', thStyle: w(150) },
        { key: 'islemTarihi',      label: 'islemTarihi',      thStyle: w(115) },
        { key: 'kiymetTakas',      label: 'kiymetTakas',      thStyle: w(115) },
      ];
    },

    mappingColumns() {
      return [
        { key: 'Isin', label: 'ISIN' },
        { key: 'Name', label: 'Name' },
      ];
    },

    filteredMappings() {
      const term = (this.mappingFilter || '').trim().toLowerCase();
      if (!term) return this.mapping.mappings;
      return this.mapping.mappings.filter(
        (m) => (m.Isin || '').toLowerCase().includes(term)
          || (m.Name || '').toLowerCase().includes(term),
      );
    },

    unmappedCount() {
      return this.rows.filter((r) => r._unmapped).length;
    },

    /**
     * Row index -> edited KIYMET, sent with the download and nowhere else.
     * The ISIN travels with each edit so the server can drop it if the trade
     * at that index changed since the preview was generated.
     */
    overrides() {
      const out = {};
      this.rows.forEach((row, i) => {
        if ((row.KIYMET || '') !== (this.originalKiymet[i] || '')) {
          out[i] = { isin: row.ISIN || '', value: row.KIYMET || '' };
        }
      });
      return out;
    },

    editedCount() {
      return Object.keys(this.overrides).length;
    },

    queryParams() {
      return {
        accounts: this.selectedAccounts.map((a) => a.AccountName),
        dateStart: this.startDate,
        dateEnd: this.endDate,
      };
    },
  },

  mounted() {
    this.getAccounts();
  },

  methods: {
    getAccounts() {
      store
        .dispatch('kareInfinaReport/getKareInfinaAccounts')
        .then((res) => {
          this.optAccounts = res.data.resData || [];
        })
        .catch((e) => {
          this.showError(`Error fetching accounts: ${e.message}`);
        });
    },

    toggleAllAccounts(checked) {
      this.selectedAccounts = checked ? [...this.optAccounts] : [];
    },

    fetchData() {
      if (this.selectedAccounts.length === 0) return;
      this.loading = true;
      this.searched = false;

      store
        .dispatch('kareInfinaReport/getKareInfinaReportData', this.queryParams)
        .then((res) => {
          this.rows = res.data.data || [];
          this.originalKiymet = this.rows.map((r) => r.KIYMET || '');
          this.searched = true;
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.searched = true;
          this.showError(`Error generating report: ${e.message}`);
        });
    },

    isEdited(index) {
      return (this.rows[index].KIYMET || '') !== (this.originalKiymet[index] || '');
    },

    resetEdits() {
      this.rows.forEach((row, i) => {
        this.$set(this.rows[i], 'KIYMET', this.originalKiymet[i]);
      });
    },

    downloadXlsx() {
      if (this.selectedAccounts.length === 0) return;
      this.downloading = true;

      store
        .dispatch('kareInfinaReport/downloadKareInfinaReportXlsx', {
          ...this.queryParams,
          overrides: this.overrides,
        })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          const now = new Date();
          const dmy = `${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}${now.getFullYear()}`;
          link.setAttribute('download', `KARE_INFINA_REPORT_${dmy}.xlsx`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.downloading = false;
        })
        .catch((e) => {
          this.downloading = false;
          this.showError(`Error downloading report: ${e.message}`);
        });
    },

    // ---- ticker mappings -------------------------------------------------

    openMappings() {
      this.mappingModal = true;
      this.clearPending();
      this.loadMappings();
    },

    loadMappings() {
      this.mappingLoading = true;
      store
        .dispatch('kareInfinaReport/getKareTickerMappings')
        .then((res) => {
          const data = res.data.data || {};
          this.mapping = {
            upload: data.upload || null,
            mappings: data.mappings || [],
            conflicts: data.conflicts || [],
          };
          this.mappingLoading = false;
        })
        .catch((e) => {
          this.mappingLoading = false;
          this.showError(`Error loading ticker mappings: ${e.message}`);
        });
    },

    clearPending() {
      this.pendingWorkbook = null;
      this.pendingSheetNames = [];
      this.pendingSheet = null;
      this.pendingRows = [];
      this.pendingConflicts = [];
      this.pendingSkipped = 0;
      this.pendingFileName = '';
      if (this.$refs.mappingFile) this.$refs.mappingFile.reset();
    },

    onMappingFile(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;

      if (typeof XLSX === 'undefined') {
        this.showError('The Excel library failed to load. Please reload the page and try again.');
        return;
      }

      this.pendingFileName = file.name;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const workbook = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
          this.pendingWorkbook = workbook;
          this.pendingSheetNames = workbook.SheetNames || [];
          this.pendingSheet = this.pendingSheetNames[0] || null;
          this.parsePendingSheet();
        } catch (err) {
          this.showError(`Could not read the Excel file: ${err.message}`);
          this.clearPending();
        }
      };
      reader.readAsArrayBuffer(file);
    },

    /**
     * Column A = ISIN, column B = name. Rows missing either are skipped, and
     * an ISIN repeated with different names is reported as a conflict with
     * the last occurrence winning - the same rule the server applies.
     */
    parsePendingSheet() {
      if (!this.pendingWorkbook || !this.pendingSheet) return;

      const sheet = this.pendingWorkbook.Sheets[this.pendingSheet];
      const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, blankrows: false });

      const parsed = [];
      let skipped = 0;

      raw.forEach((line, i) => {
        const isin = (line[0] === null || line[0] === undefined ? '' : String(line[0])).trim().toUpperCase();
        const name = (line[1] === null || line[1] === undefined ? '' : String(line[1])).trim();

        // tolerate a header row
        if (i === 0 && (isin === 'ISIN' || name.toLowerCase() === 'name')) return;

        if (!isin || !name) {
          skipped += 1;
          return;
        }
        parsed.push({ isin, name });
      });

      const winner = {};
      const seen = {};
      parsed.forEach(({ isin, name }) => {
        winner[isin] = name;
        seen[isin] = seen[isin] || [];
        if (!seen[isin].includes(name)) seen[isin].push(name);
      });

      this.pendingConflicts = Object.keys(seen)
        .filter((isin) => seen[isin].length > 1)
        .map((isin) => ({ isin, names: seen[isin], used: winner[isin] }));

      this.pendingRows = Object.keys(winner).map((isin) => ({ isin, name: winner[isin] }));
      this.pendingSkipped = skipped;

      if (this.pendingRows.length === 0) {
        this.showError('No rows with both an ISIN and a name were found on this sheet.');
      }
    },

    confirmUpload() {
      if (this.pendingRows.length === 0) return;
      this.uploading = true;

      store
        .dispatch('kareInfinaReport/uploadKareTickerMappings', {
          rows: this.pendingRows,
          fileName: this.pendingFileName,
        })
        .then((res) => {
          const data = res.data.data || {};
          this.mapping = {
            upload: data.upload || null,
            mappings: data.mappings || [],
            conflicts: data.conflicts || [],
          };
          this.uploading = false;
          this.clearPending();
          this.showSuccess(`Ticker mappings replaced (${this.mapping.upload ? this.mapping.upload.RowCount : 0} ISINs).`);
          if (this.rows.length > 0) this.fetchData();
        })
        .catch((e) => {
          this.uploading = false;
          const msg = (e.response && e.response.data && e.response.data.message) || e.message;
          this.showError(`Error uploading ticker mappings: ${msg}`);
        });
    },

    // ---- display helpers -------------------------------------------------

    rowClass(item, type) {
      if (!item || type !== 'row') return '';
      return item._unmapped ? 'table-warning' : '';
    },

    formatNumber(value, decimals) {
      if (value === null || value === '' || value === undefined) return '';
      return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },

    formatDate(value) {
      if (!value) return '';
      return moment(value).format('DD/MM/YYYY');
    },

    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Error', text: msg, icon: 'AlertTriangleIcon', variant: 'danger' },
      });
    },

    showSuccess(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Success', text: msg, icon: 'CheckIcon', variant: 'success' },
      });
    },
  },

  watch: {
    selectedAccounts(val) {
      this.allAccountsSelected = this.optAccounts.length > 0 && val.length === this.optAccounts.length;
    },

    // re-parse when the user picks a different sheet out of the workbook
    pendingSheet() {
      this.parsePendingSheet();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/src/assets/scss/compact-design.scss";

.kare-report-table {
  font-size: 0.72rem;
}

.kare-mapping-table {
  font-size: 0.75rem;
}

.card::v-deep .card-body {
  padding: 0.8rem 0.7rem !important;
}

.kiymet-input {
  font-size: 0.72rem;
  height: 1.6rem;
  padding: 0.1rem 0.3rem;
}

.kiymet-edited {
  border-color: #ff9f43;
  background-color: rgba(255, 159, 67, 0.08);
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
